import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Webhook, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const WebhooksPage = () => {
  const { API, accessToken } = useContext(AuthContext);
  const [webhooks, setWebhooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get(`${API}/webhooks`, { headers });
      setWebhooks(response.data);
    } catch (error) {
      toast.error('Failed to load webhooks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      await axios.post(
        `${API}/webhooks`,
        {
          url,
          secret,
          events: ['envelope.sent', 'envelope.completed', 'signature.applied']
        },
        { headers }
      );
      toast.success('Webhook created successfully!');
      setIsDialogOpen(false);
      setUrl('');
      setSecret('');
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to create webhook');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="webhooks-page">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900">Webhooks</h1>
            <p className="text-gray-600 mt-2">Receive real-time event notifications</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-indigo-600 to-purple-600" data-testid="create-webhook-btn">
                <Plus size={18} className="mr-2" />
                New Webhook
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Webhook</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateWebhook} className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Webhook URL</label>
                  <Input
                    type="url"
                    placeholder="https://your-domain.com/webhook"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    required
                    data-testid="input-webhook-url"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Secret</label>
                  <Input
                    type="text"
                    placeholder="your-webhook-secret"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    required
                    data-testid="input-webhook-secret"
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-webhook-btn">
                  Create Webhook
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="glass-effect border-none card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Webhook size={20} />
              Active Webhooks
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : webhooks.length > 0 ? (
              <div className="space-y-4">
                {webhooks.map((webhook) => (
                  <div key={webhook.id} className="p-4 bg-gray-50 rounded-lg" data-testid={`webhook-${webhook.id}`}>
                    <p className="font-semibold">{webhook.url}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Events: {webhook.events.join(', ')}
                    </p>
                    <span className={`inline-block mt-2 px-2 py-1 rounded text-xs ${webhook.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                      {webhook.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">No webhooks configured</p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default WebhooksPage;